import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { apiClient } from '../services/api';
import { authService } from '../services/authService';
import type {
  User,
  LoginCredentials,
  RegisterCredentials,
  AuthContextType,
  ImpersonationData,
  AuthSource,
  WsjTokenResult,
  WsjTokenError,
} from '../types/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const IMPERSONATION_KEY = 'impersonating';
const WSJ_TOKEN_KEY = 'wsj_token';
const WSJ_TOKEN_EXPIRES_KEY = 'wsj_token_expires';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating] = useState(false);

  // WSJ chat-iframe token (separate from app login; session-authed fetch).
  const [externalToken, setExternalToken] = useState<string | null>(null);
  const [authSource, setAuthSource] = useState<AuthSource | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);
  const [useUat] = useState(false);

  // Initialize auth state from the session cookie on mount.
  useEffect(() => {
    const initAuth = async () => {
      // Bootstrap the csrftoken cookie so later unsafe requests carry X-CSRFToken.
      try {
        await apiClient.get('/api/auth/csrf/');
      } catch (error) {
        console.warn('[AuthContext] CSRF bootstrap failed:', error);
      }

      // Probe the session: 200 -> authenticated; 401/403 -> anonymous.
      try {
        const me = await authService.fetchCurrentUser();
        setUser(me);
        setAuthSource('local');

        // Restore WSJ chat token from sessionStorage if present and unexpired.
        const storedExternalToken = sessionStorage.getItem(WSJ_TOKEN_KEY);
        const storedExpiresAt = sessionStorage.getItem(WSJ_TOKEN_EXPIRES_KEY);
        if (storedExternalToken) {
          const expiresAt = storedExpiresAt ? new Date(storedExpiresAt).getTime() : null;
          const isExpired = expiresAt ? Date.now() > expiresAt : false;
          if (!isExpired) {
            setExternalToken(storedExternalToken);
            if (expiresAt) {
              setSessionExpiresAt(expiresAt);
            }
          } else {
            sessionStorage.removeItem(WSJ_TOKEN_KEY);
            sessionStorage.removeItem(WSJ_TOKEN_EXPIRES_KEY);
          }
        }
      } catch {
        setUser(null);
      }

      // Stale impersonation flag (e.g. refresh during a prior session) — clear it.
      if (sessionStorage.getItem(IMPERSONATION_KEY)) {
        sessionStorage.removeItem(IMPERSONATION_KEY);
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const loggedInUser = await authService.login(credentials);
      setUser(loggedInUser);
      setAuthSource('local');
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials) => {
    try {
      // Registration creates the account but doesn't establish a session, so
      // log in with the same credentials to authenticate the user — preserving
      // the prior "registered users are logged in" behavior (consumers like
      // StripeWelcomePage advance to an authenticated step right after).
      await authService.register(credentials);
      const loggedInUser = await authService.login({
        username: credentials.username,
        password: credentials.password,
      });
      setUser(loggedInUser);
      setAuthSource('local');
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear WSJ chat token state.
    setExternalToken(null);
    setSessionExpiresAt(null);
    setAuthSource(null);
    sessionStorage.removeItem(WSJ_TOKEN_KEY);
    sessionStorage.removeItem(WSJ_TOKEN_EXPIRES_KEY);

    // Clear the server session (fire-and-forget) and local user state.
    void authService.logout();
    setUser(null);
  };

  // Impersonation is temporarily disabled: it relied on swapping a localStorage
  // auth token, which no longer exists under HttpOnly session-cookie auth. A
  // session-based reimplementation is tracked as a follow-up. These remain so
  // consumers (UserSearchModal, ImpersonationBanner, Navigation) keep compiling.
  const impersonateUser = useCallback((_data: ImpersonationData) => {
    console.warn(
      'Impersonation is temporarily disabled pending a session-based reimplementation.',
    );
  }, []);

  const exitImpersonation = useCallback(() => {
    sessionStorage.removeItem(IMPERSONATION_KEY);
  }, []);

  // Refresh user data from the server (permissions, subscription status, etc.).
  const refreshUser = useCallback(async () => {
    try {
      const freshUser = await authService.fetchCurrentUser();
      setUser(freshUser);
    } catch (error) {
      console.warn('Failed to refresh user data:', error);
    }
  }, []);

  // Fetch or refresh the WSJ token for chat iframes.
  const fetchWsjToken = useCallback(async (): Promise<WsjTokenResult> => {
    try {
      const response = await authService.fetchWsjToken();
      const expiresAt = new Date(response.expires_at).getTime();
      setExternalToken(response.token);
      setSessionExpiresAt(expiresAt);
      sessionStorage.setItem(WSJ_TOKEN_KEY, response.token);
      sessionStorage.setItem(WSJ_TOKEN_EXPIRES_KEY, response.expires_at);
      return { success: true, token: response.token, expiresAt };
    } catch (error: any) {
      const status = error.response?.status;
      let errorType: WsjTokenError = 'unknown';
      if (status === 400) {
        errorType = 'not_wsj_user';
      } else if (status === 404) {
        errorType = 'no_session';
      } else if (status === 410) {
        errorType = 'session_expired';
      } else if (status === 503) {
        errorType = 'service_unavailable';
      }
      setExternalToken(null);
      setSessionExpiresAt(null);
      sessionStorage.removeItem(WSJ_TOKEN_KEY);
      sessionStorage.removeItem(WSJ_TOKEN_EXPIRES_KEY);
      return { success: false, error: errorType };
    }
  }, []);

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isLoading,
    // External (WSJ chat) auth
    externalToken,
    authSource,
    isExternalAuth: authSource === 'external',
    sessionExpiresAt,
    useUat,
    fetchWsjToken,
    // User refresh
    refreshUser,
    // Impersonation (temporarily disabled)
    isImpersonating,
    impersonateUser,
    exitImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
